import KeyboardReturnRounded from '@mui/icons-material/KeyboardReturnRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Box, ButtonBase, Dialog, InputBase, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { visibleNav, type NavItem } from './nav';

/** ⌘K / Ctrl K: jump to any screen the admin can open. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { position: 'absolute', top: '12vh', m: 2, width: 'calc(100% - 32px)' } },
      }}
    >
      {open && <PaletteBody onClose={onClose} />}
    </Dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const results = useMemo(() => {
    const items: (NavItem & { group: string })[] = visibleNav((k) => can(k)).flatMap((g) =>
      g.items.map((i) => ({ ...i, group: g.title })),
    );
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      `${i.label} ${i.group} ${i.keywords ?? ''}`.toLowerCase().includes(q),
    );
  }, [can, query]);

  const go = (item: NavItem | undefined) => {
    if (!item) return;
    onClose();
    void navigate(item.path);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          height: 60,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SearchRounded sx={{ color: 'text.secondary' }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search screens…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              go(results[active]);
            }
          }}
          sx={{ fontSize: '1rem' }}
          inputProps={{ 'aria-label': 'Search screens' }}
        />
        <Box
          component="kbd"
          sx={{
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            px: 0.75,
            py: 0.25,
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          Esc
        </Box>
      </Box>
      <Box sx={{ maxHeight: 380, overflowY: 'auto', p: 1 }} role="listbox">
        {results.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            Nothing matches “{query}”.
          </Typography>
        )}
        {results.map((item, i) => {
          const Icon = item.icon;
          const selected = i === active;
          return (
            <ButtonBase
              key={item.path}
              role="option"
              aria-selected={selected}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(item)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                gap: 1.5,
                px: 1.5,
                height: 46,
                borderRadius: '10px',
                bgcolor: selected ? 'action.selected' : 'transparent',
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '8px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'action.hover',
                  color: selected ? 'primary.main' : 'text.secondary',
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.label}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {item.group}
              </Typography>
              {selected && <KeyboardReturnRounded sx={{ fontSize: 16, color: 'text.secondary' }} />}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
